import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiJson, ApiError, AuthError } from '@/lib/apiClient';
import type { LiveResponse } from './types';
import { liveVehiclesQueryKey } from './useLiveVehicles';
import { vehiclesQueryKey } from '../vehicles/useVehicles';
import { applyFrame, isLiveFrame, isRosterEvent } from './liveFrame';
import { createRosterRefresher } from './rosterRefresh';
import { activeSosQueryKey } from '@/features/sos/useActiveSos';

/** Seam S3 (slice 85) — the payload-free SOS hub nudge. The server pushes
 *  `{ type: 'sos' }` (tenant-routed, no body) the instant an SOS alert row is
 *  written; the client answers by re-reading `active-sos`. A dedicated predicate
 *  so a future `type`-tagged frame cannot be mistaken for it. */
function isSosNudge(parsed: unknown): boolean {
  return typeof parsed === 'object' && parsed !== null && (parsed as { type?: unknown }).type === 'sos';
}

/**
 * The live transport (seam S7). The map stops polling and starts RECEIVING: this
 * hook mints a ticket, opens the WebSocket, and applies each frame into the SAME
 * TanStack Query cache the map already reads (`liveVehiclesQueryKey`). One model,
 * two transports — there is no parallel state path. The poll (useLiveVehicles) is
 * demoted to a 60 s fallback and is disabled entirely while the socket is open.
 *
 * WHY A TICKET, NOT A COOKIE OR HEADER (PLAN §S2): a browser WebSocket cannot set
 * `Authorization`, and the refresh cookie is scoped `Path=/auth` so it never
 * reaches `/ws`. So we `POST /api/live/ticket` (a normal authenticated call),
 * get an opaque single-use 30 s ticket, and open `wss://<origin>/ws?ticket=…`.
 * EVERY connection attempt mints a FRESH ticket — a ticket is single-use and
 * expires in 30 s, so a rejected or expired ticket is not retried, it is simply
 * re-minted on the next attempt. That is why "ticket expiry/rejection handling"
 * needs no special branch: the reconnect path already re-mints.
 *
 * RECONNECT recovers STATE, not just the socket: a socket that has been away has
 * missed frames, so on every reconnect (never on the first connect — the initial
 * query fetch is the baseline) we REFETCH `/api/vehicles/live`. Continuing as if
 * nothing happened would leave the map quietly wrong, which is the whole failure
 * family this project keeps hitting.
 *
 * BOUNDED RETRY: exponential backoff with full jitter, capped, so a dead `/ws`
 * is probed at a bounded rate — never a hammering loop. While the socket is down
 * the 60 s poll keeps the map alive; the socket is the optimisation, the poll is
 * the floor.
 *
 * ZERO BYTES IN THE BACKGROUND: on `visibilitychange` to hidden the socket is
 * closed and all timers cleared; on return to visible it is re-established (which
 * refetches, because we have been away). Combined with the poll's
 * `refetchIntervalInBackground: false`, a backgrounded map on a metered
 * Mogadishu connection costs nothing.
 */

/** Backoff floor and ceiling. Constants, not env vars — every env var is a seam. */
export const RECONNECT_BASE_MS = 1_000;
export const RECONNECT_MAX_MS = 30_000;

/** Slice 18h — trailing window in which many roster events collapse to one
 * re-fetch. Matches the server's THROTTLE_WINDOW_MS so both ends coalesce alike.
 * Constant, not an env var. */
export const ROSTER_REFRESH_WINDOW_MS = 1_000;

export type LiveSocketStatus =
  | 'idle' // not connected: unsupported, paused (tab hidden), or torn down
  | 'connecting' // first attempt in flight
  | 'open' // connected and receiving
  | 'reconnecting'; // dropped, waiting out backoff before the next attempt

export interface UseLiveSocketOptions {
  /** Test seams only — real callers use the exported constants. */
  reconnectBaseMs?: number;
  reconnectMaxMs?: number;
  /** Test seam only — the roster-refetch trailing window. Real callers use
   * ROSTER_REFRESH_WINDOW_MS (1 s); tests shrink it so real timers stay fast. */
  rosterWindowMs?: number;
}

interface TicketResponse {
  ticket: string;
  expiresInSec: number;
}

function socketUrl(ticket: string): string {
  const u = new URL('/ws', window.location.href);
  u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
  u.searchParams.set('ticket', ticket);
  return u.toString();
}

export function useLiveSocket(options: UseLiveSocketOptions = {}): { status: LiveSocketStatus } {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<LiveSocketStatus>('idle');

  // Latest option values reachable from the long-lived effect without re-running
  // it (which would tear the socket down on every render).
  const baseMs = options.reconnectBaseMs ?? RECONNECT_BASE_MS;
  const maxMs = options.reconnectMaxMs ?? RECONNECT_MAX_MS;
  const baseRef = useRef(baseMs);
  baseRef.current = baseMs;
  const maxRef = useRef(maxMs);
  maxRef.current = maxMs;
  const rosterMs = options.rosterWindowMs ?? ROSTER_REFRESH_WINDOW_MS;
  const rosterMsRef = useRef(rosterMs);
  rosterMsRef.current = rosterMs;

  useEffect(() => {
    // No WebSocket (a very old browser, or jsdom): stay on the poll. The map is
    // never blank — the fallback IS the floor, not a failure.
    if (typeof WebSocket === 'undefined') {
      setStatus('idle');
      return;
    }

    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;
    // Bumped whenever we abandon an attempt (drop, pause, unmount). Every async
    // continuation (ticket resolve, onopen, onclose) captures the generation it
    // began under and no-ops if it is stale — so a socket resurrected by a race
    // can never write frames or reschedule.
    let generation = 0;
    // Permanent stop (unmount). Distinct from a hidden-tab PAUSE, which can resume.
    let stopped = false;
    // Have we ever reached `open`? The first open is the baseline (the mount
    // fetch already ran); every open AFTER that is a reconnect and must refetch.
    let everConnected = false;

    // Slice 18h — answers a server 'roster' push with one coalesced re-fetch.
    // Constructed ONCE for the life of the socket effect, disposed in cleanup.
    // 🔴 Invalidate the ['vehicles'] PREFIX, not the exact live key: it prefixes
    // ['vehicles','live'] AND ['vehicles','detail',id], so a hidden vehicle drops
    // from the live map AND its (now 404) detail page in one call. `invalidateQueries`
    // (not refetch): default refetchType 'active' marks an unmounted list stale
    // rather than fetching into a cache nobody reads. Do NOT pass `exact: true`.
    const rosterRefresher = createRosterRefresher({
      refetch: () => queryClient.invalidateQueries({ queryKey: vehiclesQueryKey }),
      windowMs: rosterMsRef.current,
    });

    const clearTimer = () => {
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const closeSocket = () => {
      if (ws) {
        ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null;
        try {
          ws.close();
        } catch {
          /* already closing */
        }
        ws = null;
      }
    };

    /** Abandon whatever is in flight without permanently stopping. */
    const abort = () => {
      generation += 1;
      clearTimer();
      closeSocket();
    };

    const scheduleReconnect = () => {
      if (stopped) {
        setStatus('idle');
        return;
      }
      // Full jitter (AWS style): delay uniform in [0, cap], cap growing
      // exponentially but bounded by maxRef — so the probe rate is bounded and a
      // fleet of tabs recovering at once does not synchronise onto `/ws`.
      const cap = Math.min(maxRef.current, baseRef.current * 2 ** attempt);
      attempt += 1;
      const delay = Math.random() * cap;
      setStatus('reconnecting');
      reconnectTimer = setTimeout(() => {
        void connect();
      }, delay);
    };

    const applyMessage = (data: unknown) => {
      if (typeof data !== 'string') return; // we only speak JSON text frames
      let parsed: unknown;
      try {
        parsed = JSON.parse(data);
      } catch {
        return;
      }
      // 🔴 SLICE 85 — THE SOS NUDGE IS CHECKED FIRST. It is payload-free and must
      // trigger an `active-sos` re-read the instant it lands (the banner/alarm
      // latency budget), never reach `applyFrame`. `invalidateQueries` so a
      // backgrounded tab marks it stale and the 15 s poll / foreground return
      // picks it up, and an open tab refetches immediately.
      if (isSosNudge(parsed)) {
        void queryClient.invalidateQueries({ queryKey: activeSosQueryKey });
        return;
      }
      // 🔴 CHECK ROSTER BEFORE isLiveFrame. A roster event announces a membership
      // change, which `applyFrame` cannot represent — it is answered by a
      // coalesced re-fetch, and must never reach `applyFrame`.
      if (isRosterEvent(parsed)) {
        rosterRefresher.notify();
        return;
      }
      if (!isLiveFrame(parsed)) return;
      // Into the SAME cache the map reads — one model, two transports.
      queryClient.setQueryData<LiveResponse>(liveVehiclesQueryKey, (old) =>
        applyFrame(old, parsed),
      );
    };

    const connect = async () => {
      if (stopped) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

      const gen = generation;
      setStatus(attempt === 0 ? 'connecting' : 'reconnecting');

      let ticket: string;
      try {
        const res = await apiJson<TicketResponse>('/api/live/ticket', { method: 'POST' });
        // 56g — a mint that returns no usable ticket is a FAILED mint, not a
        // connect: opening `/ws?ticket=undefined` is a guaranteed handshake error
        // (seen once the socket moved app-wide and a stub answered `{}`).
        if (typeof res?.ticket !== 'string' || res.ticket.length === 0) throw new Error('malformed ticket');
        ticket = res.ticket;
      } catch (err) {
        // A failed refresh is a terminal logout (apiClient already notified) —
        // stop, do not loop against an unauthenticated `/ws`.
        if (err instanceof AuthError) {
          stopped = true;
          setStatus('idle');
          return;
        }
        // 🔴 A 403 is an ANSWER, not an outage. Under a read-only impersonation
        // session (slice 17c) the mint is a POST, so it is refused
        // `read_only_session` — permanently. Backing off and re-minting would
        // retry a decision that cannot change, forever, and leave the status pill
        // stuck on "reconnecting" for the whole ten minutes.
        //
        // Stopping is not a loss of live data: `useLiveVehicles` polls
        // `/api/vehicles/live` (a GET, permitted) every 60 s whenever the socket
        // is not healthy. Staff see the customer's fleet move at 60 s resolution
        // instead of a second — the correct degradation for a view that may not
        // write.
        if (err instanceof ApiError && err.status === 403) {
          stopped = true;
          setStatus('idle');
          return;
        }
        // Any other mint failure (network/5xx): back off and re-mint.
        if (gen === generation && !stopped) scheduleReconnect();
        return;
      }

      if (stopped || gen !== generation) return; // paused/aborted while minting

      let socket: WebSocket;
      try {
        socket = new WebSocket(socketUrl(ticket));
      } catch {
        scheduleReconnect();
        return;
      }
      ws = socket;

      socket.onopen = () => {
        if (gen !== generation) {
          try {
            socket.close();
          } catch {
            /* no-op */
          }
          return;
        }
        attempt = 0;
        setStatus('open');
        if (everConnected) {
          // Been away → missed frames → reconcile against the server. Never a
          // stale continuation.
          void queryClient.refetchQueries({ queryKey: liveVehiclesQueryKey });
        }
        everConnected = true;
      };

      socket.onmessage = (ev: MessageEvent) => {
        if (gen !== generation) return;
        applyMessage(ev.data);
      };

      socket.onclose = () => {
        if (gen !== generation) return;
        ws = null;
        if (stopped) {
          setStatus('idle');
          return;
        }
        // A rejected handshake (expired/replayed ticket → server 401) surfaces
        // here too; the next connect() mints a fresh ticket. Bounded by backoff.
        scheduleReconnect();
      };

      socket.onerror = () => {
        // 'error' is always followed by 'close' in browsers — let close drive the
        // reconnect so we schedule exactly once.
      };
    };

    const onVisibility = () => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState === 'hidden') {
        abort(); // zero bytes while backgrounded
        setStatus('idle');
      } else {
        // Resume. everConnected is preserved, so the reopen refetches — coming
        // back from hidden means we have been away.
        stopped = false;
        // CHURN GUARD (BOARD.md "WebSocket reconnect churn"): a phone emits
        // redundant/duplicate 'visible' transitions. If a socket is already OPEN,
        // it is still receiving frames — do NOT open a second one over it. And if
        // an attempt is in flight or half-open, abort() FIRST so its generation is
        // retired: the orphan's later onopen closes itself and its onclose can no
        // longer schedule a reconnect (both guard on `gen === generation`). Either
        // way this branch leaves exactly one live socket, never a stack of them.
        if (ws && ws.readyState === WebSocket.OPEN) return;
        abort();
        attempt = 0;
        void connect();
      }
    };

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }
    void connect();

    return () => {
      stopped = true;
      generation += 1;
      clearTimer();
      closeSocket();
      rosterRefresher.dispose(); // 18h — cancel any pending coalesced re-fetch
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
    };
    // Create once. queryClient is stable; timing options are read through refs.
  }, [queryClient]);

  return { status };
}
